/**
 * SocialPulseFounder — /founder page.
 *
 * Full founder biography for Thomas George Vellaringattu (Tom), the
 * founder of SocialPulse®. Written in warm editorial serif and organized
 * around chapters covering Kerala origins, hospitality, entrepreneurship,
 * technology, food, the SocialPulse idea, and the AI chapter.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPin, Coffee, Rocket, Cpu, ChefHat, Sparkles,
  Bot, Camera, Compass,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

export default function SocialPulseFounder() {
  useEffect(() => {
    document.title = "Thomas George Vellaringattu — Founder of SocialPulse";
    setMeta("description", "The founder story: Thomas George (Tom) Vellaringattu — from Kerala, through hospitality, entrepreneurship, programming and pharmaceutical data, to the SocialPulse® network.");
    setLink("canonical", "https://socialpulse.com/founder");
    setMeta("og:title", "The Founder Story — Thomas George Vellaringattu", "property");
    setMeta("og:description", "How a career spanning hospitality, entrepreneurship, computing and data led to the SocialPulse network.", "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", "https://socialpulse.com/founder", "property");
    setMeta("robots", "index, follow");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-founder-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F5F1FA 0%, #EEF2FF 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-10 sm:pb-14">
          <p className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
            The Founder Story
          </p>
          <div className="mt-4 sm:mt-5 grid sm:grid-cols-[auto_minmax(0,1fr)] gap-5 sm:gap-6 items-start">
            <div
              className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden ring-4 ring-white shadow-lg shrink-0"
              data-testid="sp-founder-portrait"
              style={{ background: "linear-gradient(135deg,#F5F1FA,#ECFDF5)" }}
            >
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/8nkbgsua_IMG_0689.jpeg"
                alt="Thomas George Vellaringattu"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div>
              <h1
                className="font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
                data-testid="sp-founder-h1"
              >
                Thomas George (Tom)
                <br />
                <span className="text-emerald-700">Vellaringattu</span>
              </h1>
              <p className="mt-3 text-slate-600 text-base sm:text-lg font-medium">
                Founder of SocialPulse®
              </p>
            </div>
          </div>
          <p className="mt-6 text-slate-700 text-base sm:text-lg leading-relaxed">
            Thomas George Vellaringattu is the founder of SocialPulse®, a
            developing network of geographic, community and specialized
            digital platforms built around a central idea: useful information
            becomes more valuable when it is connected to people, places and
            communities.
          </p>
          <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed">
            His path to SocialPulse did not begin in the technology industry.
            It developed over several decades through hospitality,
            entrepreneurship, computer programming, data management and an
            enduring interest in connecting people and information.
          </p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-12 sm:py-16">

        <Chapter
          eyebrow="From Kerala to a Global Perspective"
          title="Growing up in Kadanad."
          tint="emerald"
          icon={MapPin}
        >
          <p>
            Thomas grew up in Kadanad, a village in Kerala, India. His early
            life in Kerala provided firsthand experience of the importance of
            family, villages, schools, agriculture, local businesses and
            community relationships — subjects that would eventually become
            important components of the SocialPulse concept.
          </p>
          <p>
            He studied trade cookery at the Institute of Hotel Management in
            Adyar, Chennai, and began his professional career in the hotel
            industry. That experience introduced him to an important
            principle that continued throughout his career: technology and
            business ultimately exist to serve people.
          </p>
        </Chapter>

        <Chapter
          eyebrow="From Hospitality to Entrepreneurship"
          title="Kitchens, the Middle East, and a return to Kerala."
          tint="orange"
          icon={Coffee}
        >
          <p>
            Thomas's career later took him to the Middle East, where he
            worked in catering and administrative roles.
          </p>
          <p>
            After returning to Kerala, he entered entrepreneurship and became
            involved in computer-related business during a period when
            personal computing and business information systems were still
            emerging in India. The transition from hospitality to computing
            would eventually reshape his professional life.
          </p>
        </Chapter>

        <Chapter
          eyebrow="A Career in Technology and Data"
          title="From publishing floors in New York to pharmaceutical data."
          tint="blue"
          icon={Cpu}
        >
          <p>
            After moving to the United States, Thomas worked as a computer
            programmer in New York, including positions associated with major
            publishing and financial organizations. He later built a long
            career working with data and SAS programming in the pharmaceutical
            industry.
          </p>
          <p>
            Working with structured information reinforced an idea that would
            become fundamental to SocialPulse: large amounts of information
            become useful only when they are organized, connected and made
            understandable.
          </p>
        </Chapter>

        <Chapter
          eyebrow="Food, Entrepreneurship and Community"
          title="Spice blends, a San Jose restaurant, and lessons from small businesses."
          tint="rose"
          icon={ChefHat}
        >
          <p>
            Technology was only one part of his career. Thomas also pursued
            his longstanding interest in food and entrepreneurship. He
            developed and marketed spice blends and later operated a
            restaurant in San Jose, California.
          </p>
          <p>
            These experiences provided another perspective on the challenges
            faced by small businesses, food producers and local communities —
            from marketing and customer discovery to food availability and
            waste. Many of these experiences subsequently influenced concepts
            that became part of the broader SocialPulse ecosystem.
          </p>
        </Chapter>

        <Chapter
          eyebrow="The Idea Behind SocialPulse"
          title="What if places were the backbone?"
          tint="violet"
          icon={Sparkles}
        >
          <p>
            Over time, Thomas began envisioning a platform that could
            organize information differently. Instead of building isolated
            websites for individual subjects, why not establish a common
            geographic foundation and allow many kinds of information to
            connect through it?
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-violet-500">
            <li>A village could connect to its schools, farms, families, photographs, history and discussions.</li>
            <li>A city could connect residents with food resources, businesses, events, nature observations and community organizations.</li>
            <li>A traveler could move from information about a country to a state, city, airport, attraction or local discussion.</li>
            <li>A former student could find a school and reconnect through its alumni community.</li>
            <li>Different services could remain specialized while sharing the same understanding of place.</li>
          </ul>
          <p className="italic text-slate-800 font-semibold">
            That concept became the foundation for SocialPulse.
          </p>
        </Chapter>

        <Chapter
          eyebrow="Building an Ecosystem"
          title="From one idea to a family of platforms."
          tint="teal"
          icon={Rocket}
        >
          <p>
            SocialPulse has since evolved into a broader vision involving
            geographic portals and specialized platforms addressing areas
            such as travel, agriculture, food access and food waste, nature
            documentation, alumni connections, family history, photography,
            community discussions and other locally relevant information.
          </p>
          <p>
            Rather than operating as unrelated projects, the goal is for
            these platforms to exchange appropriate information through
            shared geographic identities and common infrastructure. The
            result is intended to be a network in which a contribution made
            in one appropriate location can become useful elsewhere without
            unnecessarily duplicating the underlying information.
          </p>
          <div className="pt-2">
            <Link
              to="/network"
              data-testid="sp-founder-network-cta"
              className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-900 text-sm font-semibold"
            >
              See the network in one place <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Chapter>

        <Chapter
          eyebrow="AI and the Next Chapter"
          title="Old ideas, new scale."
          tint="lavender"
          icon={Bot}
        >
          <p>
            The rapid development of artificial intelligence created new
            possibilities for this vision. Tasks that once would have
            required very large editorial and technical teams — organizing
            geographic information, assisting with translations, improving
            accessibility, connecting related material and helping people
            navigate large collections of information — can increasingly be
            supported by AI.
          </p>
          <p>
            For Thomas, this opened an opportunity to revisit ideas
            accumulated over decades and develop them on a much larger
            scale. The aim, however, is not technology for its own sake. The
            underlying question remains:
          </p>
          <blockquote className="border-l-4 border-violet-400 pl-4 italic text-slate-800">
            How can technology make useful knowledge more accessible while
            strengthening connections between people and their communities?
          </blockquote>
        </Chapter>

        <Chapter
          eyebrow="Preserving What Communities Know"
          title="A record for future generations."
          tint="rose"
          icon={Camera}
        >
          <p>
            Another motivation behind SocialPulse is preservation. Local
            history often disappears quietly. Older photographs remain
            unidentified. Family stories are forgotten. Knowledge about
            plants and farms disappears. Former classmates lose contact.
            Small businesses close without leaving a record. Changes in
            neighborhoods and natural environments may never be documented.
          </p>
          <p>
            Digital platforms can help preserve some of this knowledge when
            people are given simple ways to contribute it. Thomas's vision
            for SocialPulse therefore combines current information with
            community memory — creating resources useful today that may also
            become valuable records for future generations.
          </p>
        </Chapter>

        <Chapter
          eyebrow="Continuing the Journey"
          title="Building connections — one place at a time."
          tint="emerald"
          icon={Compass}
        >
          <p>
            SocialPulse remains an evolving project. Its geographic portals,
            community systems and specialized platforms continue to be
            developed as new technologies and opportunities emerge.
          </p>
          <p>
            Thomas's career has moved through several seemingly different
            fields — hospitality, entrepreneurship, programming,
            pharmaceutical data, food and digital platforms — but they share
            a recurring theme: organizing resources and information so they
            can become more useful to people around the world.
          </p>
          <p>
            SocialPulse represents the continuation of that journey on a
            larger scale. Its long-term aspiration is to help connect
            communities around the world while giving individual places —
            from major countries and cities to small towns and villages — a
            meaningful place in the digital world.
          </p>
        </Chapter>

        {/* Signature card */}
        <div
          className="mt-14 rounded-2xl ring-1 ring-emerald-200 p-6 sm:p-8"
          style={{
            background:
              "linear-gradient(135deg, #ECFDF5 0%, #F5F1FA 100%)",
          }}
          data-testid="sp-founder-signature"
        >
          <p className="font-serif italic text-slate-800 text-lg">
            Thomas George Vellaringattu
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Founder, SocialPulse®
          </p>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/network"
            data-testid="sp-founder-cta-network"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
          >
            Explore the network <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/about"
            data-testid="sp-founder-cta-about"
            className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors"
          >
            Back to About
          </Link>
        </div>
      </main>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

const TINTS = {
  violet:   { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200" },
  blue:     { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-700",   ring: "ring-orange-200" },
  lavender: { bg: "bg-indigo-50",   text: "text-indigo-700",   ring: "ring-indigo-200" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-700",     ring: "ring-rose-200" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200" },
};

function Chapter({ eyebrow, title, tint, icon: Icon, children }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-9 h-9 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <p className={`text-xs font-semibold uppercase tracking-[.22em] ${t.text}`}>
          {eyebrow}
        </p>
      </div>
      <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl leading-tight">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-slate-700 text-[15px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
